import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { writeFetch } from '../../api';

const {
  FiHeart, FiStar, FiCheck, FiX, FiRefreshCw, FiSearch,
  FiTrendingUp, FiAlertTriangle, FiEdit3, FiExternalLink,
  FiDollarSign, FiUsers, FiCalendar, FiFilter,
} = FiIcons;

const TABLE = 'donation_campaigns_1776500000';
const MONET_TABLE = 'monetization_config_1776389352234';

const STATUS_COLORS = {
  active:    'bg-green-100 text-green-700 border-green-200',
  completed: 'bg-blue-100 text-blue-700 border-blue-200',
  cancelled: 'bg-red-100 text-red-700 border-red-200',
  paused:    'bg-amber-100 text-amber-700 border-amber-200',
};

const CampaignAuditTab = () => {
  const [campaigns, setCampaigns]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [search, setSearch]           = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [saving, setSaving]           = useState(null);
  const [toast, setToast]             = useState(null);
  const [featuredLimit, setFeaturedLimit] = useState(3);
  const [feeConfig, setFeeConfig]     = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [camRes, feeRes] = await Promise.all([
        writeFetch(`read/${TABLE}?limit=100&sort=created_at&order=desc`),
        writeFetch(`read/${MONET_TABLE}?category=donations&limit=20`),
      ]);
      const camData = await camRes.json();
      const feeData = await feeRes.json();
      setCampaigns(Array.isArray(camData.data) ? camData.data : []);

      const configs = Array.isArray(feeData.data) ? feeData.data : [];
      const limitRow = configs.find(c => c.config_key === 'donation_featured_campaign_limit');
      const feeRow   = configs.find(c => c.config_key === 'donation_platform_fee_pct');
      if (limitRow) setFeaturedLimit(parseInt(limitRow.config_value) || 3);
      if (feeRow)   setFeeConfig(feeRow);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleFeatured = async (campaign) => {
    const currentFeatured = campaigns.filter(c => c.is_featured == 1 || c.is_featured === true);
    const isFeatured = campaign.is_featured == 1;

    if (!isFeatured && currentFeatured.length >= featuredLimit) {
      showToast(`Maximum ${featuredLimit} featured campaigns allowed. Unfeature one first.`, 'error');
      return;
    }

    setSaving(campaign.id);
    try {
      await writeFetch(`update/${TABLE}/${campaign.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_featured: isFeatured ? 0 : 1 }),
      });
      setCampaigns(prev => prev.map(c =>
        c.id === campaign.id ? { ...c, is_featured: isFeatured ? 0 : 1 } : c
      ));
      showToast(isFeatured ? 'Campaign unfeatured' : 'Campaign featured on home page!');
    } catch { showToast('Failed to update', 'error'); }
    setSaving(null);
  };

  const updateStatus = async (campaign, status) => {
    setSaving(campaign.id + status);
    try {
      await writeFetch(`update/${TABLE}/${campaign.id}`, {
        method: 'PUT',
        body: JSON.stringify({ status }),
      });
      setCampaigns(prev => prev.map(c => c.id === campaign.id ? { ...c, status } : c));
      showToast(`Campaign ${status}`);
    } catch { showToast('Failed to update', 'error'); }
    setSaving(null);
  };

  const filtered = campaigns.filter(c => {
    const q = search.toLowerCase();
    const matchSearch = !q || c.title?.toLowerCase().includes(q) || c.memorial_name?.toLowerCase().includes(q) || c.beneficiary_name?.toLowerCase().includes(q);
    const matchStatus = statusFilter === 'all' || c.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const featuredCount = campaigns.filter(c => c.is_featured == 1).length;
  const totalRaised   = campaigns.reduce((s, c) => s + (Number(c.raised_amount) || 0), 0);
  const activeCnt     = campaigns.filter(c => c.status === 'active').length;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-accent-navy to-[#1a2a5e] text-white rounded-[32px] p-8 flex items-center gap-6 border border-white/10 shadow-2xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-5" style={{ backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '28px 28px' }} />
        <div className="w-16 h-16 bg-accent-gold text-accent-navy rounded-2xl flex items-center justify-center shadow-xl flex-shrink-0 z-10">
          <SafeIcon icon={FiHeart} className="w-8 h-8" />
        </div>
        <div className="z-10 flex-1">
          <div className="flex items-center gap-3 mb-1">
            <h2 className="font-serif text-2xl">Campaign Audit & Management</h2>
            <span className="text-[9px] px-3 py-1 bg-accent-gold text-accent-navy rounded-full font-black uppercase tracking-widest">Super Admin</span>
          </div>
          <p className="text-white/60 text-sm font-light">Monitor all live campaigns, feature top causes on the home page, and audit donation flows.</p>
        </div>
        <button onClick={load} className="p-3 bg-white/10 hover:bg-white/20 rounded-2xl transition-all z-10">
          <SafeIcon icon={FiRefreshCw} className={`w-5 h-5 text-white ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Campaigns', value: campaigns.length,         icon: FiHeart,       color: 'text-pink-600',    bg: 'bg-pink-50'    },
          { label: 'Active Now',      value: activeCnt,                icon: FiTrendingUp,  color: 'text-green-600',   bg: 'bg-green-50'   },
          { label: 'Featured',        value: `${featuredCount}/${featuredLimit}`, icon: FiStar, color: 'text-yellow-600', bg: 'bg-yellow-50' },
          { label: 'Total Raised',    value: `$${totalRaised.toLocaleString()}`, icon: FiDollarSign, color: 'text-indigo-600', bg: 'bg-indigo-50' },
        ].map(s => (
          <div key={s.label} className={`${s.bg} rounded-[24px] border border-brand-dark p-5 text-center`}>
            <div className="w-10 h-10 mx-auto mb-3 rounded-xl flex items-center justify-center bg-white border border-brand-dark">
              <SafeIcon icon={s.icon} className={`w-5 h-5 ${s.color}`} />
            </div>
            <p className={`text-xl font-serif font-bold ${s.color}`}>{s.value}</p>
            <p className="text-[10px] font-black uppercase tracking-widest text-text-muted mt-1">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Featured limit info */}
      <div className="flex items-start gap-4 p-5 bg-amber-50 border border-amber-200 rounded-2xl">
        <SafeIcon icon={FiStar} className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-bold text-amber-800 mb-1">Featured Campaigns on Home Page</p>
          <p className="text-xs text-amber-700 leading-relaxed">
            You can feature up to <strong>{featuredLimit}</strong> campaigns at once. They will appear as "Top Charity Causes" on the public home page.
            Currently featuring <strong>{featuredCount}</strong>. Adjust the limit in Monetization → Donations settings.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search campaigns…"
            className="w-full pl-11 pr-4 py-3 bg-white border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm" />
        </div>
        <div className="flex gap-2">
          {['all', 'active', 'completed', 'paused', 'cancelled'].map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className={`px-4 py-3 rounded-2xl text-[9px] font-black uppercase tracking-widest border-2 transition-all ${
                statusFilter === s ? 'bg-accent-navy text-white border-accent-navy' : 'border-brand-dark text-text-muted hover:border-accent-navy/40'
              }`}>{s}</button>
          ))}
        </div>
      </div>

      {/* Campaign list */}
      {loading ? (
        <div className="grid grid-cols-1 gap-4">
          {[...Array(3)].map((_, i) => <div key={i} className="h-36 bg-white rounded-[28px] border-2 border-brand-dark animate-pulse" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-[40px] p-16 border border-brand-dark text-center">
          <div className="w-20 h-20 bg-brand-light rounded-full flex items-center justify-center mx-auto mb-6">
            <SafeIcon icon={FiHeart} className="w-10 h-10 text-accent-gold/40" />
          </div>
          <h3 className="text-2xl font-serif text-accent-navy mb-2">No campaigns found</h3>
          <p className="text-text-muted font-light">Try adjusting your search or filters.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map(campaign => {
            const goal    = Number(campaign.goal_amount) || 0;
            const raised  = Number(campaign.raised_amount) || 0;
            const pct     = goal > 0 ? Math.min(Math.round((raised / goal) * 100), 100) : 0;
            const isFeatured = campaign.is_featured == 1;
            const daysLeft   = campaign.deadline ? Math.max(0, Math.ceil((new Date(campaign.deadline) - new Date()) / 86400000)) : null;

            return (
              <motion.div key={campaign.id} layout
                className={`bg-white border-2 rounded-[28px] p-6 transition-all ${isFeatured ? 'border-accent-gold shadow-lg shadow-accent-gold/10' : 'border-brand-dark hover:border-accent-navy/30'}`}>
                <div className="flex flex-col md:flex-row gap-4">
                  {/* Cover */}
                  {campaign.cover_image_url && (
                    <div className="w-full md:w-24 h-20 rounded-2xl overflow-hidden flex-shrink-0">
                      <img src={campaign.cover_image_url} alt="" className="w-full h-full object-cover" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 mb-2 flex-wrap">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-serif text-lg text-accent-navy leading-tight">{campaign.title}</h3>
                          {isFeatured && <span className="text-[9px] px-2 py-0.5 bg-accent-gold text-white rounded-full font-black uppercase tracking-widest">⭐ Featured</span>}
                        </div>
                        <p className="text-[10px] text-text-muted mt-0.5">
                          {campaign.memorial_name && <span className="text-accent-navy font-bold">For {campaign.memorial_name} · </span>}
                          {campaign.beneficiary_type?.replace('_', ' ')} · {campaign.beneficiary_name}
                        </p>
                      </div>
                      <span className={`text-[9px] px-3 py-1 rounded-full font-black uppercase tracking-widest border ${STATUS_COLORS[campaign.status] || STATUS_COLORS.active}`}>
                        {campaign.status || 'active'}
                      </span>
                    </div>

                    {/* Progress */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex-1 h-2 bg-brand-light rounded-full overflow-hidden">
                        <div className="h-full bg-accent-gold rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                      <span className="text-[10px] font-black text-accent-navy whitespace-nowrap">${raised.toLocaleString()} / ${goal.toLocaleString()} ({pct}%)</span>
                    </div>

                    {/* Meta */}
                    <div className="flex items-center gap-4 text-[10px] text-text-muted mb-4 flex-wrap">
                      <span className="flex items-center gap-1"><SafeIcon icon={FiUsers} className="w-3 h-3" /> {campaign.donor_count || 0} donors</span>
                      {daysLeft !== null && <span className="flex items-center gap-1"><SafeIcon icon={FiCalendar} className="w-3 h-3" /> {daysLeft}d left</span>}
                      <span>ID: {campaign.id}</span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 flex-wrap">
                      <button onClick={() => toggleFeatured(campaign)} disabled={saving === campaign.id}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border-2 transition-all ${
                          isFeatured
                            ? 'bg-accent-gold/10 border-accent-gold text-accent-gold hover:bg-red-50 hover:border-red-400 hover:text-red-600'
                            : 'border-brand-dark text-text-muted hover:border-accent-gold hover:text-accent-gold'
                        }`}>
                        {saving === campaign.id
                          ? <div className="w-3 h-3 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                          : <SafeIcon icon={FiStar} className="w-3 h-3" />
                        }
                        {isFeatured ? 'Unfeature' : 'Feature'}
                      </button>

                      {campaign.status === 'active' && (
                        <button onClick={() => updateStatus(campaign, 'paused')}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border-2 border-brand-dark text-text-muted hover:border-amber-400 hover:text-amber-600 transition-all">
                          Pause
                        </button>
                      )}
                      {campaign.status === 'paused' && (
                        <button onClick={() => updateStatus(campaign, 'active')}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border-2 border-brand-dark text-text-muted hover:border-green-400 hover:text-green-600 transition-all">
                          Reactivate
                        </button>
                      )}
                      {!['cancelled', 'completed'].includes(campaign.status) && (
                        <button onClick={() => updateStatus(campaign, 'cancelled')}
                          className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest border-2 border-brand-dark text-text-muted hover:border-red-400 hover:text-red-600 transition-all">
                          Cancel
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div initial={{ opacity: 0, y: 20, x: '-50%' }} animate={{ opacity: 1, y: 0, x: '-50%' }} exit={{ opacity: 0, y: 20, x: '-50%' }}
            className={`fixed bottom-8 left-1/2 z-[9999] flex items-center gap-3 px-6 py-4 rounded-2xl shadow-2xl text-white text-sm font-bold ${toast.type === 'error' ? 'bg-red-600' : 'bg-green-600'}`}>
            <SafeIcon icon={toast.type === 'error' ? FiAlertTriangle : FiCheck} className="w-4 h-4" />
            {toast.msg}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CampaignAuditTab;